import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getBannersApi, createBannerApi, updateBannerApi, deleteBannerApi, toggleBannerApi, uploadBannerImageApi } from '../../api/adminApi';

const BLANK = { title: '', subtitle: '', image_url: '', link_url: '', bg_color: '#111111', sort_order: 0 };

export default function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(BLANK);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    getBannersApi()
      .then((r) => setBanners(r.data))
      .catch(() => toast.error('Failed to load banners'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(BLANK); setModal(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ title: b.title, subtitle: b.subtitle || '', image_url: b.image_url || '', link_url: b.link_url || '', bg_color: b.bg_color || '#111111', sort_order: b.sort_order || 0 }); setModal(true); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setUploading(true);
    try {
      const r = await uploadBannerImageApi(fd);
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success('Image uploaded to /uploads/banners/');
    } catch { toast.error('Image upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Banner title is required');
    setSaving(true);
    try {
      if (editing) {
        const r = await updateBannerApi(editing, form);
        setBanners((prev) => prev.map((b) => b.id === editing ? r.data : b));
        toast.success('Banner updated');
      } else {
        const r = await createBannerApi(form);
        setBanners((prev) => [...prev, r.data]);
        toast.success('Banner created');
      }
      setModal(false);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      const r = await toggleBannerApi(id);
      setBanners((prev) => prev.map((b) => b.id === id ? r.data : b));
    } catch { toast.error('Toggle failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await deleteBannerApi(id);
      setBanners((prev) => prev.filter((b) => b.id !== id));
      toast.success('Banner deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Banners</h1>
          <p style={S.sub}>{banners.length} banners · {banners.filter((b) => b.is_active).length} active</p>
        </div>
        <button style={S.btnAdd} onClick={openAdd}>+ Add Banner</button>
      </div>

      {/* Banner cards */}
      {loading
        ? <div style={S.loading}><div style={S.spinner} /></div>
        : banners.length === 0
          ? (
            <div style={S.empty}>
              <p style={{ fontSize: '32px', marginBottom: '12px' }}>🖼</p>
              <p style={{ fontWeight: 700, color: '#374151', marginBottom: '8px' }}>No banners yet</p>
              <p style={{ color: '#9CA3AF', fontSize: '13px' }}>Add your first homepage banner to get started.</p>
              <button style={{ ...S.btnAdd, marginTop: '16px' }} onClick={openAdd}>+ Add Banner</button>
            </div>
          )
          : (
            <div style={S.grid}>
              {banners.map((b) => (
                <div key={b.id} style={S.card}>
                  {/* Preview */}
                  <div style={{ ...S.preview, background: b.bg_color || '#111' }}>
                    {b.image_url
                      ? <img src={b.image_url} alt={b.title} style={S.previewImg} />
                      : (
                        <div style={S.previewText}>
                          <p style={{ fontFamily: 'var(--font-display)', fontSize: '18px', letterSpacing: '2px', color: '#fff', margin: 0 }}>{b.title}</p>
                          {b.subtitle && <p style={{ fontSize: '11px', color: 'rgba(255,255,255,.7)', margin: '4px 0 0 0' }}>{b.subtitle}</p>}
                        </div>
                      )
                    }
                    <span style={{ ...S.activePill, background: b.is_active ? '#DCFCE7' : '#FEE2E2', color: b.is_active ? '#16A34A' : '#DC2626' }}>
                      {b.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </div>

                  {/* Info */}
                  <div style={S.cardBody}>
                    <div style={{ flex: 1 }}>
                      <p style={S.cardTitle}>{b.title}</p>
                      {b.subtitle && <p style={S.cardSub}>{b.subtitle}</p>}
                      {b.link_url && <p style={S.cardLink}>↗ {b.link_url}</p>}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                        <div style={{ width: '14px', height: '14px', borderRadius: '2px', background: b.bg_color, border: '1px solid #E5E5E5' }} />
                        <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{b.bg_color}</span>
                        <span style={{ fontSize: '11px', color: '#9CA3AF' }}>· Order: {b.sort_order}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button style={S.iconBtn} onClick={() => handleToggle(b.id)} title={b.is_active ? 'Hide' : 'Show'}>
                        {b.is_active ? '👁' : '🙈'}
                      </button>
                      <button style={S.iconBtn} onClick={() => openEdit(b)} title="Edit">✏️</button>
                      <button style={{ ...S.iconBtn, color: '#DC2626' }} onClick={() => handleDelete(b.id)} title="Delete">🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
      }

      {/* Modal */}
      {modal && (
        <div style={S.overlay} onClick={() => setModal(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <span style={S.modalTitle}>{editing ? 'EDIT BANNER' : 'NEW BANNER'}</span>
              <button style={S.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>

            {/* Live preview */}
            <div style={{ ...S.preview, background: form.bg_color || '#111', height: '80px', marginBottom: '16px' }}>
              {form.image_url
                ? <img src={form.image_url} alt="preview" style={S.previewImg} />
                : (
                  <div style={S.previewText}>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '2px', color: '#fff', margin: 0 }}>{form.title || 'Banner Title'}</p>
                    {form.subtitle && <p style={{ fontSize: '11px', color: 'rgba(255,255,255,.7)', margin: '2px 0 0 0' }}>{form.subtitle}</p>}
                  </div>
                )
              }
            </div>

            <div style={S.formGrid}>
              <div style={S.field}>
                <label style={S.label}>Title *</label>
                <input style={S.input} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. MONSOON COLLECTION" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Subtitle</label>
                <input style={S.input} value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="e.g. Shop the new season drop" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Image</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input style={{ ...S.input, flex: 1 }} value={form.image_url}
                    onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                    placeholder="Paste URL or upload →" />
                  <label style={{ ...S.uploadBtn, opacity: uploading ? .6 : 1, cursor: uploading ? 'default' : 'pointer' }}>
                    {uploading ? '...' : '↑ Upload'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '3px 0 0' }}>Saved to <code>uploads/banners/</code></p>
              </div>
              <div style={S.field}>
                <label style={S.label}>Link URL</label>
                <input style={S.input} value={form.link_url} onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))} placeholder="/shop or /shop?category=..." />
              </div>
              <div style={S.field}>
                <label style={S.label}>Background Color</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="color" value={form.bg_color} onChange={(e) => setForm((f) => ({ ...f, bg_color: e.target.value }))} style={{ width: '40px', height: '36px', border: 'none', cursor: 'pointer', padding: 0 }} />
                  <input style={{ ...S.input, flex: 1 }} value={form.bg_color} onChange={(e) => setForm((f) => ({ ...f, bg_color: e.target.value }))} placeholder="#111111" />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Sort Order</label>
                <input type="number" style={S.input} value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} min="0" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={S.btnCancel} onClick={() => setModal(false)}>Cancel</button>
              <button style={S.btnSave} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Banner'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  hdr:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' },
  title:       { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:         { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  btnAdd:      { background: '#111', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
  loading:     { display: 'flex', justifyContent: 'center', padding: '60px 0' },
  spinner:     { width: '36px', height: '36px', border: '3px solid #E5E5E5', borderTopColor: 'var(--red)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty:       { background: '#fff', border: '2px dashed #E5E5E5', padding: '60px', textAlign: 'center' },
  grid:        { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '16px' },
  card:        { background: '#fff', border: '1px solid #E5E5E5', boxShadow: '0 1px 3px rgba(0,0,0,.04)', overflow: 'hidden' },
  preview:     { position: 'relative', height: '120px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  previewImg:  { width: '100%', height: '100%', objectFit: 'cover' },
  previewText: { textAlign: 'center', padding: '16px' },
  activePill:  { position: 'absolute', top: '8px', right: '8px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '99px' },
  cardBody:    { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px' },
  cardTitle:   { fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '1px', color: '#111', margin: 0 },
  cardSub:     { fontSize: '12px', color: '#6B7280', margin: '2px 0 0 0' },
  cardLink:    { fontSize: '11px', color: '#3B82F6', margin: '4px 0 0 0' },
  iconBtn:     { background: '#F9FAFB', border: '1px solid #E5E5E5', padding: '6px 9px', cursor: 'pointer', fontSize: '14px', lineHeight: 1 },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal:       { background: '#fff', width: '560px', maxWidth: '96vw', maxHeight: '90vh', overflow: 'auto', padding: '28px' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle:  { fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '2px', color: '#111' },
  closeBtn:    { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', lineHeight: 1, padding: '4px 8px' },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' },
  field:       { display: 'flex', flexDirection: 'column', gap: '5px' },
  label:       { fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6B7280' },
  input:       { border: '1.5px solid #E5E5E5', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none' },
  btnCancel:   { background: '#fff', border: '1.5px solid #E5E5E5', padding: '9px 20px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', color: '#666' },
  btnSave:     { background: '#111', color: '#fff', border: 'none', padding: '10px 22px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
  uploadBtn:   { background: '#F3F4F6', border: '1.5px solid #E5E5E5', padding: '0 14px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' },
};
